import React, { useState, useCallback, useEffect } from "react";
import {
    GoogleMap,
    Marker,
    DirectionsRenderer,
    useJsApiLoader,
} from "@react-google-maps/api";
import { GoogleMapsApiKey } from "../constants/FormateDate";
import { localeDateEndDate, localeDateStartDate } from "../constants/DateUtil/DateUtil";
import { postStoreRiderJobPickupOrDelivery } from "../services/StoreRiderInfo/StoreRiderInfo";
import { getUserData } from "../utils/localStorage";
import { useNavigate } from "react-router-dom";

const { innerWidth: width, innerHeight: height } = window;

const containerStyle = {
    width: "50%",
    height: height - 50,
};


// Single source of truth for the pickup (green) vs delivery (orange) theme,
// used by the map markers/routes and the list panels below so everything
// stays visually consistent.
const THEME = {
    pickup: {
        main: "#1a9e5c",
        dark: "#0f7a44",
        tint: "#eafaf1",
        border: "#bfeed3",
        markerIcon: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    },
    delivery: {
        main: "#e8710a",
        dark: "#c05a02",
        tint: "#fff3e8",
        border: "#fbd7b0",
        markerIcon: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
    },

    store: {
        main: "#0a93e8",
        dark: "#024ec0",
        tint: "#e8f9ff",
        border: "#b0d0fb",
        markerIcon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    },
};

const getInitials = (name = "") =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";

const isValidCoordinate = (lat, lng) => {
    const latitude = Number(lat);
    const longitude = Number(lng);

    if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude) ||
        latitude < -90 ||
        latitude > 90 ||
        longitude < -180 ||
        longitude > 180
    ) {
        return false;
    }

    if (latitude === 0 && longitude === 0) {
        return false;
    }

    return true;
};

// A single row in the pickup/delivery list panels.
function CustomerRow({ customer, colors }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 10,
                background: colors.tint,
                border: `1px solid ${colors.border}`,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: colors.main,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 600,
                        flexShrink: 0,
                    }}
                >
                    {getInitials(customer.customerName)}
                </div>
                <div style={{ minWidth: 0 }}>
                    <p
                        style={{
                            margin: 0,
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: "#1f2430",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {customer.customerName || "Unnamed customer"}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                        {customer.customerMobile || "—"}
                    </p>
                </div>
            </div>

            <span
                style={{
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.dark,
                    background: "#fff",
                    border: `1px solid ${colors.main}`,
                    padding: "3px 9px",
                    borderRadius: 999,
                    whiteSpace: "nowrap",
                }}
            >
                {customer.timeSlot || "No slot"}
            </span>
        </div>
    );
}

// A pickup or delivery list panel (header + count pill + rows).
function DataPanel({ title, data, colors }) {
    // if (data.length === 0) return null;

    return (
        <div
            className="flex-1 m-3"
            style={{
                background: "#fff",
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid #eef0f2",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "14px 18px",
                    borderBottom: `3px solid ${colors.main}`,
                }}
            >
                <span
                    style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: colors.main,
                        flexShrink: 0,
                    }}
                />
                <span style={{ fontWeight: 600, fontSize: 15, color: "#1f2430" }}>
                    {title}
                </span>
                {data.length > 0 && <span
                    style={{
                        marginLeft: "auto",
                        fontSize: 12,
                        fontWeight: 700,
                        color: colors.dark,
                        background: colors.tint,
                        padding: "2px 10px",
                        borderRadius: 999,
                    }}
                >
                    {data.length}
                </span>}
            </div>

            <div
                style={{
                    padding: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    maxHeight: height /4,
                    overflowY: "auto",
                }}
            >
                {data.map((customer, index) => (
                    <CustomerRow
                        key={`${title}-${index}`}
                        customer={customer}
                        colors={colors}
                    />
                ))}
            </div>
        </div>
    );
}

export default function PickupDeliveryMap() {

    const navigate = useNavigate(); 

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GoogleMapsApiKey,
    });

    const { storeData } = getUserData();

    const storeId = storeData.id;

    // Store Location
    const store = {
        lat: storeData.address.latitude || 28.6139,
        lng: storeData.address.longitude || 77.2090,
    };


    let initialStartDate = new Date();
    initialStartDate.setDate(initialStartDate.getDate() - 30); // subtract 15 days from current date

    const [startDate, setStartDate] = useState(new Date(initialStartDate));

    let initialEndDate = new Date();
    const [endDate, setEndDate] = useState(new Date(initialEndDate));

    const [pickupDirections, setPickupDirections] = useState(null);
    const [optimizedPickupCustomers, setOptimizedPickupCustomers] = useState([]);

    const [deliveryDirections, setDeliveryDirections] = useState(null);
    const [optimizedDeliveryCustomers, setOptimizedDeliveryCustomers] = useState([]);

    const [pickupData, setPickupData] = useState([]);
    const [deliveryData, setDeliveryData] = useState([]);

    const fetchRiderJobPickupData = (data) => {
        postStoreRiderJobPickupOrDelivery(storeId, "pickup", data)
            .then((response) => {
                if (!response.data.error) {
                    const Res = response.data;
                    const newRes = Res.slice(-25)
                        .map((item) => {
                            const address = item?.pickupRequest?.addressDTO;

                            return {
                                type: "pickup",
                                customerName: item?.pickupRequest?.customerName,
                                customerMobile: item?.pickupRequest?.customerMobile,
                                timeSlot: item?.pickupRequest?.pickupTimeSlot,
                                lat: Number(address?.latitude),
                                lng: Number(address?.longitude),
                            };
                        })
                        // FIX: object has `lat`/`lng`, not `latitude`/`longitude`
                        .filter((item) => isValidCoordinate(item.lat, item.lng));
                    setPickupData(newRes);
                }
            })
            .catch((err) => {
                console.error("Rider Job Error:", err);
            });
    };

    const fetchRiderJobDeliveryData = (data) => {
        postStoreRiderJobPickupOrDelivery(storeId, "delivery", data)
            .then((response) => {
                if (!response.data.error) {
                    const Res = response.data;
                    const newRes = Res.slice(-25)
                        .map((item) => {
                            const address = item?.deliveryRequest?.addressDTO;

                            return {
                                type: "delivery",
                                customerName: item?.deliveryRequest?.order?.customerName,
                                customerMobile: item?.deliveryRequest?.order?.customerMobile,
                                timeSlot: item?.deliveryRequest?.deliveryTimeSlot,
                                lat: Number(address?.latitude),
                                lng: Number(address?.longitude),
                            };
                        })
                        // FIX: same lat/lng vs latitude/longitude mismatch
                        .filter((item) => isValidCoordinate(item.lat, item.lng));
                    setDeliveryData(newRes);
                }
            })
            .catch((err) => {
                console.error("Rider Job Error:", err);
            });
    };

    useEffect(() => {
        const pickupPayload = {
            startDate: localeDateStartDate(new Date(startDate)),
            endDate: localeDateEndDate(new Date(endDate)),
            jobStatus: [
                "PICKUP_REQ",
                "ASSIGNED_TO_RIDER",
                "RESCHEDULE_BY_CUSTOMER",
                "RESCHEDULE_BY_RIDER",
            ],
        };

        fetchRiderJobPickupData(pickupPayload);

        const deliveryPayload = {
            startDate: localeDateStartDate(new Date(startDate)),
            endDate: localeDateEndDate(new Date(endDate)),
            jobStatus: [
                "DELIVERY_REQ",
                "ASSIGNED_TO_RIDER",
                "RESCHEDULE_BY_CUSTOMER",
                "RESCHEDULE_BY_RIDER",
                "OUT_FOR_DELIVERY",
            ],
        };

        fetchRiderJobDeliveryData(deliveryPayload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // FIX: depend on pickupData so the route recalculates once real data arrives,
    // instead of forever using the empty array captured on first render.
    const calculatePickupRoute = useCallback(() => {
        if (!window.google || pickupData.length === 0) return;

        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
            {
                origin: store,
                destination: store, // round trip back to the store
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: true,
                waypoints: pickupData.map((customer) => ({
                    location: {
                        lat: customer.lat,
                        lng: customer.lng,
                    },
                    stopover: true,
                })),
            },
            (result, status) => {
                if (status === "OK") {
                    setPickupDirections(result);

                    // Get optimized order
                    const order = result.routes[0].waypoint_order;

                    // FIX: the waypoints came from pickupData, so the optimized
                    // order must be mapped back onto pickupData, not the
                    // unrelated static `customers` array.
                    const sortedCustomers = order.map((i) => pickupData[i]);

                    setOptimizedPickupCustomers(sortedCustomers);
                } else {
                    console.log(status);
                }
            }
        );
    }, [pickupData]);

    // Delivery route: same pattern as pickup, run against deliveryData and
    // its own directions/marker state so the two routes don't overwrite
    // each other.
    const calculateDeliveryRoute = useCallback(() => {
        if (!window.google || deliveryData.length === 0) return;

        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
            {
                origin: store,
                destination: store, // round trip back to the store
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: true,
                waypoints: deliveryData.map((customer) => ({
                    location: {
                        lat: customer.lat,
                        lng: customer.lng,
                    },
                    stopover: true,
                })),
            },
            (result, status) => {
                if (status === "OK") {
                    setDeliveryDirections(result);

                    const order = result.routes[0].waypoint_order;
                    const sortedCustomers = order.map((i) => deliveryData[i]);

                    setOptimizedDeliveryCustomers(sortedCustomers);
                } else {
                    console.log(status);
                }
            }
        );
    }, [deliveryData]);

    useEffect(() => {
        if (isLoaded && pickupData.length > 0) {
            calculatePickupRoute();
        }
    }, [isLoaded, pickupData, calculatePickupRoute]);

    useEffect(() => {
        if (isLoaded && deliveryData.length > 0) {
            calculateDeliveryRoute();
        }
    }, [isLoaded, deliveryData, calculateDeliveryRoute]);

    if (!isLoaded) return <>Loading...</>;

    return (
        <>
            <div className='h-3rem surface-0 sticky z-2 top-0 flex justify-content-center align-items-center' style={{
                "--surface-0": "#1752AA"
            }}>
                <div className='flex-1 flex ml-3 mr-3'>
                    <div className="flex-1 flex " >
                        <div className="ml-3 flex justify-content-center align-items-center" >
                            <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                                <i className="pi pi-angle-left" onClick={() => navigate(-1)} style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}></i>
                            </div>
                        </div>
                        <div className="flex-1 ml-5" >
                            <p className=" text-2xl text-white font-bold capitalize">
                                Rider Route</p>
                        </div>
                    </div>

                     

                </div>
            </div>
        <div className="flex">
            <GoogleMap mapContainerStyle={containerStyle} center={store} zoom={11}>
                {/* Store Marker */}
                    <Marker position={store} label="S" title="Store" 
                    icon={
                        window.google
                            ? { url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"}
                             : undefined
                            }
                     />

                {/* Optimized Pickup Markers (green) */}
                {optimizedPickupCustomers.map((customer, index) => (
                    <Marker
                        key={`pickup-${index}`}
                        position={{
                            lat: customer.lat,
                            lng: customer.lng,
                        }}
                        label={`P${index + 1}`}
                        title={`Pickup: ${customer.customerName} - ${customer.timeSlot}`}
                        icon={
                            window.google
                                ? { url: THEME.pickup.markerIcon }
                                : undefined
                        }
                    />
                ))}

                {/* Optimized Delivery Markers (orange) */}
                {optimizedDeliveryCustomers.map((customer, index) => (
                    <Marker
                        key={`delivery-${index}`}
                        position={{
                            lat: customer.lat,
                            lng: customer.lng,
                        }}
                        label={`D${index + 1}`}
                        title={`Delivery: ${customer.customerName} - ${customer.timeSlot}`}
                        icon={
                            window.google
                                ? { url: THEME.delivery.markerIcon }
                                : undefined
                        }
                    />
                ))}

                {/* Pickup Route (green) */}
                {pickupDirections && (
                    <DirectionsRenderer
                        options={{
                            directions: pickupDirections,
                            suppressMarkers: true,
                            polylineOptions: {
                                strokeColor: THEME.pickup.main,
                            },
                        }}
                    />
                )}

                {/* Delivery Route (orange) */}
                {deliveryDirections && (
                    <DirectionsRenderer
                        options={{
                            directions: deliveryDirections,
                            suppressMarkers: true,
                            polylineOptions: {
                                strokeColor: THEME.delivery.main,
                            },
                        }}
                    />
                )}
            </GoogleMap>

            <div className="flex-1">
                    <DataPanel title="Store" data={[]} colors={THEME.store} />
                    <DataPanel title="Pickups" data={pickupData} colors={THEME.pickup} />
                    <DataPanel title="Deliveries" data={deliveryData} colors={THEME.delivery} />
            </div>
            </div>
        </>
    );
}
